"use client";

import type { CSSProperties, ReactElement } from "react";
import { useMemo } from "react";
import {
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from "../Cmarkdownvisual";
import { TimelineControls } from "./TimelineControls";
import { TimelineViewport } from "./TimelineViewport";
import { TimelineVisualFrame } from "./TimelineVisualFrame";
import { parseMermaidTimeline } from "./parse-mermaid-timeline";
import { selectTimelineContainerMetrics } from "./timeline-root-selectors";
import { useTimelineRootModel } from "./useTimelineRootModel";
import type { ParsedTimeline } from "./timeline-types";

export interface CmarkdowntimelineProps {
  content: string;
  className?: string;
  style?: CSSProperties;
}

interface TimelineRootProps {
  className?: string;
  parsed: ParsedTimeline | null;
  style?: CSSProperties;
}

function TimelineRoot({
  className,
  parsed,
  style,
}: TimelineRootProps): ReactElement {
  const model = useTimelineRootModel({ parsed });
  const layout = model.metrics.layout;
  if (!parsed) {
    return (
      <CmarkdownvisualError
        dataElScope="Cmarkdowntimeline/TimelineRoot"
        type={CmarkdownvisualType.Timeline}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  if (!layout || parsed.periods.length === 0) {
    return (
      <CmarkdownvisualError
        dataElScope="Cmarkdowntimeline/TimelineRoot"
        type={CmarkdownvisualType.Timeline}
        reason={CmarkdownvisualErrorReason.Empty}
      />
    );
  }

  const containerMetrics = selectTimelineContainerMetrics({
    chartHeight: layout.chartHeight,
  });
  const controls = (
    <TimelineControls
      onScrollNext={model.scrollController.handleScrollNext}
      onScrollPrev={model.scrollController.handleScrollPrev}
      scrollHintState={model.scrollController.scrollHintState}
    />
  );

  return (
    <TimelineVisualFrame
      className={className}
      compactPreview={model.metrics.compactPreview}
      containerHeight={containerMetrics.containerHeight}
      controls={controls}
      style={style}
      totalEvents={model.metrics.totalEvents}
    >
      <TimelineViewport
        layout={layout}
        model={model}
        parsed={parsed}
        viewportHeight={containerMetrics.viewportHeight}
      />
    </TimelineVisualFrame>
  );
}

export function Cmarkdowntimeline({
  content,
  className,
  style,
}: CmarkdowntimelineProps): ReactElement {
  const parsed = useMemo(() => parseMermaidTimeline(content), [content]);

  return <TimelineRoot className={className} parsed={parsed} style={style} />;
}
